import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Folder from "@mapmycustomers/shared/types/entity/Folder";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { Dropdown, SearchField } from "@mapmycustomers/ui";

import { fetchFolders } from "@app/store/resources/actions";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { ResourceFieldName } from "@app/util/fieldModel/ResourceFieldModel";
import {
  createSimpleCondition,
  getSimpleConditionForField,
  removeSimpleConditionForFields,
  upsertSimpleConditions,
} from "@app/util/filters/utils";

import RoundButton from "../../RoundButton";

import styles from "./Toolbar.module.scss";

const CLEAR_OPTION_VALUE = "clear";

interface FolderFilterProps {
  filter: FilterModel;
  onChange: (filter: FilterModel) => void;
}

const FolderFilter = ({
  filter,
  onChange,
  onFetchFolders,
}: FolderFilterProps & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const [folders, setFolders] = useState<Folder[]>([]);
  const [searchText, setSearchText] = useState("");

  const selectedFolderId: Folder["id"] | undefined = useMemo(() => {
    const folderCondition = getSimpleConditionForField(filter, ResourceFieldName.FOLDER);
    return folderCondition?.operator === FilterOperator.EQUALS ? folderCondition.value : undefined;
  }, [filter]);

  const selectedFolderName = useMemo(() => {
    if (selectedFolderId) {
      return folders.find(({ id }) => id === selectedFolderId)?.name;
    }
    return undefined;
  }, [folders, selectedFolderId]);

  const handleMenuClick = useCallback(
    (item: MenuInfo) => {
      const key = item.key as string | typeof CLEAR_OPTION_VALUE;
      onChange(
        key === CLEAR_OPTION_VALUE
          ? removeSimpleConditionForFields(filter, ResourceFieldName.FOLDER)
          : upsertSimpleConditions(
              filter,
              createSimpleCondition(
                ResourceFieldName.FOLDER,
                FilterOperator.EQUALS,
                parseInt(key, 10)
              )
            )
      );
      analytics.clicked(["Resource Folder"]);
    },
    [analytics, onChange, filter]
  );

  const folderItems = useMemo(() => {
    return [
      {
        className: styles.clearMenuItem,
        key: CLEAR_OPTION_VALUE,
        label: intl.formatMessage({
          id: "component.selectResourcesModal.folderFilter.clear",
          defaultMessage: "Clear",
          description: "Clear option in filter component",
        }),
      },
      ...folders.map(({ id, name }) => ({
        key: id,
        label: name,
      })),
    ];
  }, [folders, intl]);

  useEffect(() => {
    onFetchFolders({ callback: setFolders, query: searchText });
  }, [onFetchFolders, searchText]);

  return (
    <Dropdown
      dropdownRender={(menu) => (
        <div className={styles.dropdown}>
          <SearchField
            allowClear
            className={styles.searchField}
            onChange={setSearchText}
            placeholder={intl.formatMessage({
              id: "component.selectResourcesModal.folderFilter.searchFolders",
              defaultMessage: "Search Folders",
              description: "Search folders placeholder",
            })}
            value={searchText}
          />
          <div className={styles.menu}>{menu}</div>
        </div>
      )}
      menu={{
        items: folderItems,
        onClick: handleMenuClick,
        selectedKeys: selectedFolderId ? [String(selectedFolderId)] : [],
      }}
      trigger={["click"]}
    >
      <RoundButton
        active={!!selectedFolderId}
        className={styles.folderFilter}
        rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
      >
        <span className={styles.folderName}>
          {selectedFolderName ??
            intl.formatMessage({
              id: "component.selectResourcesModal.folderFilter.allResources",
              defaultMessage: "All Resources",
              description: "Folder filter button when no folder is selected",
            })}
        </span>
      </RoundButton>
    </Dropdown>
  );
};

const mapDispatchToProps = {
  onFetchFolders: fetchFolders.request,
};

export default connect(null, mapDispatchToProps)(FolderFilter);
